import { useCallback } from 'react';
import { type FlagConfiguration, type Flag, useFlagsService } from '@atlassian/jira-flags';
import {
	MAPPING_FIELD_UPDATE_SUCCESS_FLAG_ID,
	MAPPING_HIERARCHY_UPDATE_SUCCESS_FLAG_ID,
	MAPPING_STATUS_UPDATE_SUCCESS_FLAG_ID,
	OAUTH_UNSUCCESS_FLAG_ID,
} from '../../constants';
import messages from './messages';

const getSuccessFlag = (props?: Partial<FlagConfiguration>): Flag => ({
	id: MAPPING_FIELD_UPDATE_SUCCESS_FLAG_ID,
	type: 'success',
	isAutoDismiss: true,
	title: '',
	...props,
});

const getOAuthUnsuccessFlag = (props?: Partial<FlagConfiguration>): Flag => ({
	id: OAUTH_UNSUCCESS_FLAG_ID,
	type: 'info',
	isAutoDismiss: true,
	title: '',
	...props,
});

const getFlagById = (flagId: string, props?: Partial<FlagConfiguration>) => {
	switch (flagId) {
		case MAPPING_FIELD_UPDATE_SUCCESS_FLAG_ID:
			return getSuccessFlag({ title: messages.mappingFieldUpdateFlagTitle });
		case MAPPING_HIERARCHY_UPDATE_SUCCESS_FLAG_ID:
			return getSuccessFlag({ title: messages.mappingHierarchyUpdateFlagTitle });
		case MAPPING_STATUS_UPDATE_SUCCESS_FLAG_ID:
			return getSuccessFlag({
				id: MAPPING_STATUS_UPDATE_SUCCESS_FLAG_ID,
				title: messages.mappingStatusUpdateFlagTitle,
			});
		case OAUTH_UNSUCCESS_FLAG_ID:
			return getOAuthUnsuccessFlag(props);
		default:
			return null;
	}
};

export type UseX2CFlag = {
	onShowFlag: (flagId: string, props?: Partial<FlagConfiguration>) => void;
	onDismissFlag: (flagId: string) => void;
};

export const useX2CFlag = (): UseX2CFlag => {
	const { showFlag, hasFlag, dismissFlag } = useFlagsService();
	const onShowFlag = useCallback(
		(selectedFlagId: string, props?: Partial<FlagConfiguration>) => {
			if (hasFlag?.(selectedFlagId)) {
				return;
			}
			const flag = getFlagById(selectedFlagId, props);
			if (flag) {
				showFlag(flag);
			}
		},
		[hasFlag, showFlag],
	);

	const onDismissFlag = useCallback(
		(selectedFlagId: string) => {
			if (!hasFlag?.(selectedFlagId)) {
				return;
			}
			dismissFlag(selectedFlagId);
		},
		[dismissFlag, hasFlag],
	);

	return { onShowFlag, onDismissFlag };
};
